import { LocalDate } from "@js-joda/core";
import { match, P } from "ts-pattern";
import { ComplianceFormQuestion, ComplianceFormQuestionId } from "../../../messages";
import { ComplianceFormQuestionState } from "./useComplianceFormStage";

export function filterNestedQuestions(
  questions: ComplianceFormQuestion[]
): ComplianceFormQuestionState[] {
  const questionMap = new Map<ComplianceFormQuestionId, ComplianceFormQuestion>(
    questions.map((q) => [q.id, q])
  );

  return questions.map((q) => {
    if (q.parentId === null) {
      return {
        ...q,
        isHidden: false,
      };
    }

    const parent = questionMap.get(q.parentId);

    if (parent !== undefined) {
      if (
        !["radio", "yesno"].includes(parent.type) ||
        (parent.type === "radio" && parent.value === q.parentOptionId) ||
        (parent.type === "yesno" && parent.value === q.parentBooleanValue)
      ) {
        return {
          ...q,
          isHidden: false,
        };
      }
    }

    questionMap.delete(q.id);

    return {
      ...q,
      isHidden: true,
    };
  });
}

export function mapQuestionWithAnswer(
  question: ComplianceFormQuestion,
  answers: Record<string, string | undefined>
): ComplianceFormQuestion {
  const answerValue = answers[`question-${question.id}`] ?? null;

  return match(question)
    .with({ type: P.union("text", "phone_number", "signature") }, (q) => ({
      ...q,
      value: answerValue,
    }))
    .with({ type: "radio" }, (q) => {
      const selectedOptionId = q.options.find((option) => option.id.toString() === answerValue)?.id;

      return {
        ...q,
        value: selectedOptionId ?? null,
      };
    })
    .with({ type: "date" }, (q) => ({
      ...q,
      value: answerValue === null ? null : LocalDate.parse(answerValue),
    }))
    .with({ type: "yesno" }, (q) => ({
      ...q,
      value: answerValue === "true",
    }))
    .with({ type: "plain_text" }, (q) => q)
    .with({ type: "document" }, (q) => ({
      ...q,
      value: answerValue,
    }))
    .exhaustive();
}

export function getIsValidAnswer(question: ComplianceFormQuestion) {
  switch (question.type) {
    case "document":
      return question.value !== null;

    case "date":
    case "phone_number":
    case "plain_text":
    case "radio":
    case "signature":
    case "text":
    case "yesno":
      return true;
  }
}
